import { HttpMethods } from "@/utils/constant";
import authAxiosServices from "@/service/axios/authAxios";
import hwmAxios from "@/service/axios/hwmAxios";

// HWM 전화번호 인증 발송 (공생폐 배출신고 전용, 무토큰 공개 API)
// purpose는 서버가 site_household_waste_reception으로 고정하므로 보내지 않는다.
// 응답: { success, verification_id, expires_at, message }
export const requestHwmPhoneVerification = async (phone) => {
  const url = `zguhada/v1/phone-verification/request/`;

  const response = await hwmAxios({
    method: HttpMethods.post,
    url: url,
    data: {
      phone: phone,
    },
  });

  return response.data;
};

// HWM 전화번호 인증 확인 — 코드 불일치는 403으로 온다.
// 응답: { success, verified_at, message }
export const verifyHwmPhoneVerification = async (verificationId, code) => {
  const url = `zguhada/v1/phone-verification/verify/`;

  const response = await hwmAxios({
    method: HttpMethods.post,
    url: url,
    data: {
      verification_id: verificationId,
      code: code,
    },
  });

  return response.data;
};

// HWM 세션 인증 발송 (지구하다 공용 단일 인증 축, 무토큰 공개 API)
// 검증 성공 시 발급되는 세션 하나로 대생폐 접수·대생폐 내역조회·공생폐 내역조회를
// 모두 게이트한다. 공생폐 접수용 zguhada/v1/phone-verification/* (소진 축)와 별개
// 엔드포인트 — purpose는 서버가 ZGUHADA_SESSION 으로 고정한다.
// 응답: { success, verification_id, expires_at, message }
export const requestHwmSessionVerification = async (phone) => {
  const url = `zguhada/v1/phone-verification/session/request/`;

  const response = await hwmAxios({
    method: HttpMethods.post,
    url: url,
    data: {
      phone: phone,
    },
  });

  return response.data;
};

// HWM 세션 인증 확인 — 코드 불일치는 403.
// 성공 시 30분짜리 무소진 세션 토큰이 발급된다(복수 접수·조회 재사용 가능,
// 대생폐/공생폐 내역조회 공용).
// 응답: { success, verified_at, session_token, session_expires_in(초), message }
export const verifyHwmSessionVerification = async (verificationId, code) => {
  const url = `zguhada/v1/phone-verification/session/verify/`;

  const response = await hwmAxios({
    method: HttpMethods.post,
    url: url,
    data: {
      verification_id: verificationId,
      code: code,
    },
  });

  return response.data;
};

// SMS 인증번호 요청
export const putCellPhoneAuth = async (cellphone, recaptchaToken = null) => {
  const url = `/common/cellphoneauth/simple/`;

  const response = await authAxiosServices({
    method: HttpMethods.put,
    url: url,
    data: {
      cellphone: cellphone,
      ...(recaptchaToken && { recaptcha_token: recaptchaToken }),
    },
  });

  return response.data;
};

// 인증번호 인증 요청
export const postCellphoneAuth = async (id, authNo) => {
  const url = `/zguhada/cellphone/auth/`;

  const response = await authAxiosServices({
    method: HttpMethods.post,
    url: url,
    data: {
      id: id,
      auth_no: authNo,
    },
  });

  return response.data;
};

// 전화번호 로그인
export const postPhoneNumberLogin = async (cellphone, password, recaptchaToken = null) => {
  try {
    const url = `/zguhada/login/number/`;

    const response = await authAxiosServices({
      method: HttpMethods.post,
      url: url,
      data: {
        cellphone: cellphone,
        password: password,
        ...(recaptchaToken && { recaptcha_token: recaptchaToken }),
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

// sns 회원가입
export const postSnsRegister = async ({
  name,
  phone_auth_id,
  social_type,
  social_id,
  social_refresh,
  old_14,
  personal_info,
  service_use,
  location_info,
  marketing_push,
  marketing_sms,
  recaptcha_token = null,
}) => {
  try {
    const url = `/zguhada/register/sns/`;

    const response = await authAxiosServices({
      method: HttpMethods.post,
      url: url,
      data: {
        name: name,
        phone_auth_id: phone_auth_id,
        social_type: social_type,
        social_id: social_id,
        social_refresh: social_refresh,
        old_14: old_14,
        personal_info: personal_info,
        service_use: service_use,
        location_info: location_info,
        marketing_push: marketing_push,
        marketing_sms: marketing_sms,
        ...(recaptcha_token && { recaptcha_token }),
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const postRegister = async ({
  phone_auth_id,
  name,
  password,
  old_14,
  personal_info,
  service_use,
  location_info,
  marketing_push,
  marketing_sms,
  recaptcha_token = null,
}) => {
  try {
    const response = await authAxiosServices({
      method: HttpMethods.post,
      url: `/zguhada/register2/`,
      data: {
        phone_auth_id: phone_auth_id,
        name: name,
        password: password,
        old_14: old_14,
        personal_info: personal_info,
        service_use: service_use,
        location_info: location_info,
        marketing_push: marketing_push,
        marketing_sms: marketing_sms,
        ...(recaptcha_token && { recaptcha_token }),
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const postPasswordAuth = async ({ user_id, user_name }) => {
  try {
    const response = await authAxiosServices({
      method: HttpMethods.post,
      url: `/zguhada/password/auth/`,
      data: {
        user_id: user_id,
        user_name: user_name,
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const postPasswordChange = async ({ phone_auth_id, password }) => {
  try {
    const url = `zguhada/password/change/`;

    const response = await authAxiosServices({
      method: HttpMethods.post,
      url: url,
      data: {
        phone_auth_id: phone_auth_id,
        password: password,
      },
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};
